'use strict';

import * as GC from '@grapecity/spread-sheets';
import { isEmpty } from '@helpers/arrays';
import { sharedRegex } from '@helpers/spread/shared-regex';
import { BalanceSheet } from '@models/balance-sheet';
import { BalanceSheetService } from '@services/balance-sheet/balance-sheet.service';
import {
  BalanceSheetCategory,
  NON_MATURITY_DEPOSIT_INDEX,
  NON_MATURITY_DEPOSIT_NET_CHANGE_INDEX,
  AUXILIARY_ASSET_INDEX,
  AUXILIARY_LIABILITY_INDEX,
} from '@models/balance-sheet-category';
import { PROCESSING_ROWS_THRESHOLD } from './constants';
import '@helpers/spread/formula-list-validator';
import { NgZone } from '@angular/core';

const COL_CATEGORY = 0;
const COL_ACCOUNT_NAME = 1;
const COL_MNEMONIC = 2;
const COL_ACCOUNT_NUMBER = 3;
const COL_PARENT_ACC = 4;
const COL_DISPLAY_ORDER = 5;
const COL_FILTER_CRITERIA = 6;

export const isEmptyRow = (r: number, data: BalanceSheet[]) => {
  const row = data[r];
  const emptyRow =
    JSON.stringify(row) === JSON.stringify({}) ||
    isEmpty(row) ||
    (isEmpty(row.Category) &&
      isEmpty(row.AccountName) &&
      isEmpty(row.Mnemonic) &&
      isEmpty(row.AccountNumber) &&
      isEmpty(row.ParentAcctNum));

  return emptyRow;
};

export const suspendAll = (sheet: GC.Spread.Sheets.Worksheet, origin: string = null): boolean => {
  let suspendState = false;
  try {
    suspendState = sheet.isPaintSuspended();

    if (!suspendState || origin === 'setValidations') {
      sheet.suspendPaint();
    }
  } catch {}
  return suspendState;
};

export const resumeAll = (sheet: GC.Spread.Sheets.Worksheet, origin: string = null, suspendState: boolean = false, isLoading = false) => {
  try {
    if (sheet && (origin === 'setValidations' || (!suspendState && !isLoading))) {
      sheet.resumePaint();
    }
  } catch {}
};

// export const setValidations = (sheet: GC.Spread.Sheets.Worksheet, data: BalanceSheet[], isProcessing: boolean) => {
export const setValidations = (component: any) => {
  const { data, sheet } = component;
  let { isProcessing } = component;

  // Error handling when quickly switching tabs
  try {
    if (data.length > PROCESSING_ROWS_THRESHOLD) {
      isProcessing = true;
    }

    suspendAll(sheet, `setValidations`);

    for (const index in data) {
      if (data[index]) {
        addValidationsToRow(component, Number(index));
      }
    }

    resumeAll(sheet, `setValidations`);
  } catch {}

  isProcessing = false;
  return isProcessing;
};

/**
 *
 * @param r Row index (0 based)
 */
export const validateRow = (
  sheet: GC.Spread.Sheets.Worksheet,
  data: BalanceSheet[],
  zone: NgZone,
  balanceSheetService: BalanceSheetService,
  r: number
) => {
  const { Category, AccountName, Mnemonic, AccountNumber, ParentAcctNum, DisplayOrder, FilterCriteria } = data[r];

  let rowIsEmpty = false;
  let validCategory = false;
  let validAccountName = false;
  let validMnemonic = false;
  let validAccountNumber = false;
  let validParentAccountNumber = false;
  let validDisplayOrder = false;
  let validFilterCriteria = false;

  rowIsEmpty = isEmptyRow(r, data);
  if (!rowIsEmpty) {
    validCategory = sheet.isValid(r, COL_CATEGORY, Category);
    if (validCategory) {
      validAccountName = sheet.isValid(r, COL_ACCOUNT_NAME, AccountName);
      if (validAccountName) {
        validMnemonic = sheet.isValid(r, COL_MNEMONIC, Mnemonic);
        if (validMnemonic) {
          validAccountNumber = sheet.isValid(r, COL_ACCOUNT_NUMBER, AccountNumber);
          validParentAccountNumber = sheet.isValid(r, COL_PARENT_ACC, ParentAcctNum);
          if (validParentAccountNumber) {
            validDisplayOrder = sheet.isValid(r, COL_DISPLAY_ORDER, DisplayOrder);
            if (validDisplayOrder) {
              validFilterCriteria = sheet.isValid(r, COL_FILTER_CRITERIA, FilterCriteria);
              if (!validFilterCriteria) {
              }
            }
          }
        }
      }
    }
  }

  // Row is also valid if empty
  const valid =
    rowIsEmpty ||
    (validCategory &&
      validAccountName &&
      validMnemonic &&
      validAccountNumber &&
      validParentAccountNumber &&
      validDisplayOrder &&
      validFilterCriteria);

  // So ngOnChange gets triggered and invalidRows update is detected
  zone.run(() => {
    if (valid) {
      // Remove this row from the invalidRows array
      balanceSheetService.invalidRows = [...balanceSheetService.invalidRows].filter(x => x !== r + 1);
    } else {
      // Add this row to the invalidRows array
      balanceSheetService.invalidRows = [...balanceSheetService.invalidRows, r + 1];
    }
  });

  return valid;
};

export const addCategoryValidatorToRow = (component: any, r: number) => {
  const { sheet, total, categoryNamesString, validationMessages, categoryNames } = component;

  // ================
  //   CATEGORY
  // ================
  //
  // Row is an existing category (/api/balancesheet/categories)
  // "Investment","Loan", ... ,"Other Liability"
  //
  // The lowest level account either needs to be a category that isn’t “total”,
  // or its parent needs to be a category that isn’t “total”.
  // If an items child is a category including the category of “total” then it must be a “total”.
  //
  // =====================================================================================
  //
  //  * (J3) Account category:  =IF(LEN(A3)>0,A3,"")
  //  * (I3) Parent  category:  =IF(ISNUMBER(MATCH(E3,D:D,0)),INDIRECT(ADDRESS(MATCH(E3,D:D,0),1)),"ROOT")
  //
  //  CONDITIONS: =AND(condition1,condition2,condition3,condition4,condition5)
  //
  // 📏 (condition1) No child-parent with both empty categories
  //    =OR(LEN(J3)>0, LEN(I3)>0)
  //
  // 📏 (condition2) If child category Empty, parent's category is non-total
  //    =NOT(AND(LEN(J3)=0,I3="Total"))
  //
  // 📏 (condition3) If child category non-empty and non-total, parent's category is total
  //    =NOT(AND(AND(LEN(J3)>0,J3<>"Total"),I3<>"Total"))
  //
  // 📏 (condition4) If child category is Total, parent's category is Total or Root
  //    =NOT(AND(J3="Total",AND(I3<>"Total",I3<>"ROOT")))
  //
  // 📏 (condition5) Valid Category Name
  //    =OR(LEN(J3)=0, ISNUMBER(MATCH(J3,{"Investment","Loan",...,"Other Liability","Total"},0)))
  //
  // 📏 (condition6) Row is empty
  //    =OR(COUNTBLANK(A${row}:G${row})=7, AND(COUNTBLANK(A${row}:G${row})=6, F${row}=0))
  //
  // =====================================================================================

  const row = r + 1;

  const conditions = [
    {
      description: `A parent and a child account and cannot both have an empty category.`,
      formula: `OR(LEN(IF(LEN(A${row})>0,A${row},""))>0, LEN(IF(ISNUMBER(MATCH(E${row},D:D,0)),
      INDIRECT(ADDRESS(MATCH(E${row},D:D,0),1)),"ROOT"))>0)`,
    },
    {
      description: `A Child account cannot have an empty category when its parent is a Total Account.`,
      formula: `NOT(AND(LEN(IF(LEN(A${row})>0,A${row},""))=0,IF(ISNUMBER(MATCH(E${row},D:D,0)),
      INDIRECT(ADDRESS(MATCH(E${row},D:D,0),1)),"ROOT")="${total}"))`,
    },
    {
      description: `If a category is selected for this account its parent’s category must be “Total”`,
      formula: `NOT(AND(AND(LEN(IF(LEN(A${row})>0,A${row},""))>0,IF(LEN(A${row})>0,A${row},"")<>"${total}"),
      IF(ISNUMBER(MATCH(E${row},D:D,0)),INDIRECT(ADDRESS(MATCH(E${row},D:D,0),1)),"ROOT")<>"${total}"))`,
    },
    {
      description: `If the category selected for this account is “Total” then it’s parent’s category must also be “Total”.`,
      formula: `NOT(AND(IF(LEN(A${row})>0,A${row},"")="${total}",AND(IF(ISNUMBER(MATCH(E${row},D:D,0)),
      INDIRECT(ADDRESS(MATCH(E${row},D:D,0),1)),"ROOT")<>"${total}",IF(ISNUMBER(MATCH(E${row},D:D,0)),
      INDIRECT(ADDRESS(MATCH(E${row},D:D,0),1)),"ROOT")<>"ROOT")))`,
    },
    {
      description: `Invalid category name`,
      formula: `OR(LEN(IF(LEN(A${row})>0,A${row},""))=0, ISNUMBER(MATCH(IF(LEN(A${row})>0,A${row},""),{${categoryNamesString}},0)))`,
    },
    {
      description: `Empty row`,
      formula: `OR(
        COUNTBLANK(A${row}:G${row})=7,
        AND(
          COUNTBLANK(A${row}:G${row})=6,
          F${row}=0
        )
      )`,
    },
  ];

  let condition = `
    OR(
      ${conditions[5].formula},
      AND(
        ${conditions[0].formula},
        ${conditions[1].formula},
        ${conditions[2].formula},
        ${conditions[3].formula},
        ${conditions[4].formula}
      )
    )
  `;
  condition = condition = condition.replace(/  |\r\n|\n|\r/gm, '');
  const categoryValidator = GC.Spread.Sheets.DataValidation.createFormulaValidator(condition);
  categoryValidator.ignoreBlank(false);
  categoryValidator.inCellDropdown(true);
  categoryValidator.type(GC.Spread.Sheets.DataValidation.CriteriaType.list);
  const con = categoryValidator.condition();
  con.expected(categoryNames.join(','));
  con._useExpectedAsList = true;
  sheet.setDataValidator(r, 0, 1, 1, categoryValidator);

  validationMessages[`${row}-${0}`] = [];

  const isValidCell = sheet.isValid(r, 0, sheet.getValue(r, 0));

  if (!isValidCell) {
    conditions.forEach((cond, i) => {
      if (i < 5) {
        const value = sheet.getValue(r, 0);
        const validator = GC.Spread.Sheets.DataValidation.createFormulaValidator(cond.formula);
        validator.ignoreBlank(false);
        sheet.setDataValidator(r, 0, 1, 1, validator);
        const valid = sheet.isValid(r, 0, value);
        if (!valid) {
          validationMessages[`${row}-${0}`].push(`• ${cond.description}`);
        }
      }
    });
  }

  sheet.setDataValidator(r, 0, 1, 1, categoryValidator);
};

export const addMnemonicValidatorToRow = (sheet: GC.Spread.Sheets.Worksheet, r: number) => {
  // const validCell = false;
  const row = r + 1;
  const col = 2;

  // ================
  //     MNEMONIC
  // ================
  // Row is non-empty, cell is non-empty, and unique in its column
  // Mnemonic filed should only accept letters, numbers, underscores (_). It should not allow spaces.
  // Hyphens are restricted (bug fix 1957)

  let condition = `
  OR(
    OR(COUNTBLANK(A${row}:G${row})=7, AND(COUNTBLANK(A${row}:G${row})=6, F${row}=0)),
    AND(
      NOT(ISBLANK(TRIM(C${row}))),
      NOT(OR(ISNUMBER(SEARCH("~?",C${row})), ISNUMBER(SEARCH("~*",C${row})), ISNUMBER(SEARCH("~~",C${row})))),
      COUNTIF(C:C,C${row}) = 1,
      IF(C${row}="",TRUE,IF(ISERROR(SUMPRODUCT(SEARCH(MID(C${row},
        ROW(INDIRECT("1:"&LEN(C${row}))),1),
        "${sharedRegex.mnemonicAcceptedCharacters}"))),FALSE,TRUE))
    )
  )
  `;

  // Removing new lines and tabs
  condition = condition.replace(/  |\r\n|\n|\r/gm, '');

  const MnemonicValidation = GC.Spread.Sheets.DataValidation.createFormulaValidator(condition);

  MnemonicValidation.ignoreBlank(false);
  sheet.setDataValidator(r, col, 1, 1, MnemonicValidation);
};

export const addAccountNameValidatorToRow = (sheet: GC.Spread.Sheets.Worksheet, r: number) => {
  const row = r + 1;

  // ================
  //   ACCOUNT NAME
  // ================
  // Row is non-empty, cell is non-empty, and unique in its column
  const AccountNameValidator = GC.Spread.Sheets.DataValidation.createFormulaValidator(
    `OR(OR(COUNTBLANK(A${row}:G${row})=7, AND(COUNTBLANK(A${row}:G${row})=6, F${row}=0)), NOT(LEN(B${row})=0))`
  );

  sheet.setDataValidator(r, 1, 1, 1, AccountNameValidator);
  AccountNameValidator.ignoreBlank(false);
};

export const addAccountNumberValidatorToRow = (sheet: GC.Spread.Sheets.Worksheet, r: number) => {
  let validCell = false;
  const col = 3; // Account #
  const row = r + 1;
  // ==================
  //   ACCOUNT NUMBER
  // ==================
  //
  // Row is empty, OR
  //   Cell is a number >= 0 AND
  //   Cell is a number <=  9,223,372,036,854,775,807 (Int64, reduced to 18 digits) AND
  //   either
  //   -> Unique in its column OR
  //   -> has Filter Criteria AND has no children

  let condition = `
  OR(
    OR(COUNTBLANK(A${row}:G${row})=7, AND(COUNTBLANK(A${row}:G${row})=6, F${row}=0)),
    AND(
      ISNUMBER(D${row}),
      D${row}>=0,
      D${row}<=999999999999999999,
      OR(
        COUNTIF(D:D,D${row})=1,
        AND(
          NOT(LEN(G${row})=0),
          COUNTIF(E:E,D${row})=0
        )
      )
    )
  )
  `;

  condition = condition = condition.replace(/  |\r\n|\n|\r/gm, '');

  const accNumValidator = GC.Spread.Sheets.DataValidation.createFormulaValidator(condition);

  sheet.setDataValidator(r, col, 1, 1, accNumValidator);
  accNumValidator.ignoreBlank(false);

  const value = sheet.getCell(r, col).value();
  validCell = sheet.isValid(r, col, value);
  accNumValidator.showInputMessage(!validCell);
};

export const addParentAccountNumberValidatorToRow = (sheet: GC.Spread.Sheets.Worksheet, r: number) => {
  const row = r + 1;
  // ==================
  //   PARENT ACCOUNT
  // ==================
  // Cell is non-empty AND is unique in Account # column AND (PARENT account number and account number should be different) )
  // Parent cannot be an auxiliary account
  // OR cell is -1
  // OR row is empty

  const differentAccountAndParentNumberCondition = `D${row} <> E${row}`;
  const parentIsNotAuxiliary = `NOT(ISNUMBER(SEARCH("Auxiliary",INDIRECT(ADDRESS(MATCH(E${row},D:D,0),1)))))`;

  let condition = `OR(
    AND(
      E${row} <> "",
      COUNTIF(D:D,E${row}) = 1,
      ${differentAccountAndParentNumberCondition},
      ISNUMBER(E${row}),
      E${row}>=0,
      E${row}<=999999999999999999,
      ${parentIsNotAuxiliary}
    ),
    E${row} = -1,
    OR(
      COUNTBLANK(A${row}:G${row})=7,
      AND(
        COUNTBLANK(A${row}:G${row})=6,
        F${row}=0
      )
    )
  )`;

  condition = condition = condition.replace(/  |\r\n|\n|\r/gm, '');

  const ValidParentValidation = GC.Spread.Sheets.DataValidation.createFormulaValidator(condition);

  sheet.setDataValidator(r, 4, 1, 1, ValidParentValidation);
  ValidParentValidation.ignoreBlank(false);
};

export const addDisplayOrderValidatorToRow = (sheet: GC.Spread.Sheets.Worksheet, r: number) => {
  const col = 5; // Account #
  const row = r + 1;
  const value = sheet.getCell(r, 5).value();

  // =================
  //   DISPLAY ORDER
  // =================
  // Row is empty, or cell is a number >= 0, or cell is empty
  // Spec: vii.	The Display Order should be numeric or blank.
  // If it isn’t it will result in the following validation message: “The display order should be numeric or blank”.
  const DisplayOrderValidation = GC.Spread.Sheets.DataValidation.createFormulaValidator(
    // `OR(COUNTBLANK(A${row}:G${row})=7, AND(COUNTBLANK(A${row}:G${row})=6, F${row}=0)),
    //    AND(OR(AND(ISNUMBER(F${row}),F${row}>=0),ISBLANK(F${row}))))`
    `OR(F${row}=0,AND(OR(AND(ISNUMBER(F${row}),F${row}>=0),ISBLANK(F${row}))))`
  );

  DisplayOrderValidation.ignoreBlank(false);
  sheet.setDataValidator(r, col, 1, 1, DisplayOrderValidation); // Display Order
};

interface ParentAccDict {
  [acc: number]: string;
}
const parentCategoryDict: ParentAccDict = {};

export const addFilterCriteriaValidatorToRow = (sheet: GC.Spread.Sheets.Worksheet, r: number, categories: BalanceSheetCategory[]) => {
  // Note: This can be implemented with less javascript but a big SpreadJS formula.

  // USER_WEIGHT can only be applied to Non Maturity Deposits or its children
  const nmdCondition = new GC.Spread.Sheets.ConditionalFormatting.Condition(
    GC.Spread.Sheets.ConditionalFormatting.ConditionType.textCondition,
    { compareType: GC.Spread.Sheets.ConditionalFormatting.TextCompareType.doesNotContain }
  );
  nmdCondition.expected('*USER_WEIGHT*');
  nmdCondition.useWildCards(true);
  nmdCondition.ignoreBlank(true);
  nmdCondition.ignoreCase(true);
  const nmdFilterCriteriaValidator = new GC.Spread.Sheets.DataValidation.DefaultDataValidator(nmdCondition);
  nmdFilterCriteriaValidator.type(GC.Spread.Sheets.DataValidation.CriteriaType.custom);

  // No filter criteria can be applied to Auxiliary Accounts
  const auxAccFilterCriteriaValidator = GC.Spread.Sheets.DataValidation.createTextLengthValidator(
    GC.Spread.Sheets.ConditionalFormatting.ComparisonOperators.equalsTo,
    0,
    0
  );

  // Add only to those cells that are not non-maturity deposits or its children
  try {
    // Extract the string that corresponds with NMD category
    const nmdCategoryText = categories.find(cat => cat.id === NON_MATURITY_DEPOSIT_INDEX).name;
    const nmdNetChangeCategoryText = categories.find(cat => cat.id === NON_MATURITY_DEPOSIT_NET_CHANGE_INDEX).name;
    const auxAssetCategoryText = categories.find(cat => cat.id === AUXILIARY_ASSET_INDEX).name;
    const auxLiabilityCategoryText = categories.find(cat => cat.id === AUXILIARY_LIABILITY_INDEX).name;

    // Extract current row category text and account number
    let accCategory = sheet.getCell(r, COL_CATEGORY).value();
    const accNum = sheet.getCell(r, COL_ACCOUNT_NUMBER).value();

    // If this row has a category, then it's a parent account
    if (accCategory) {
      // Add its details to the dictionary [accNum: category string]
      parentCategoryDict[accNum] = accCategory;

      // Then add the appropriate validator depending on the category
      switch (accCategory) {
        case nmdCategoryText:
        case nmdNetChangeCategoryText:
          sheet.getCell(r, COL_FILTER_CRITERIA).validator(null);
          break;
        case auxAssetCategoryText:
        case auxLiabilityCategoryText:
          sheet.getCell(r, COL_FILTER_CRITERIA).validator(auxAccFilterCriteriaValidator);
          break;
        default:
          sheet.getCell(r, COL_FILTER_CRITERIA).validator(nmdFilterCriteriaValidator);
      }
    } else {
      // If it's child account (row category = null)
      // Get parent account number
      const parentAccNum = sheet.getCell(r, COL_PARENT_ACC).value();

      // Try to find it on the dictionary
      if (!(parentAccNum in parentCategoryDict)) {
        // If not there, iterate over the cells until it finds a match, then add it to the dictionary
        for (let i = 0; i <= sheet.getRowCount(); i++) {
          if (sheet.getCell(i, COL_ACCOUNT_NUMBER) === parentAccNum) {
            accCategory = sheet.getCell(i, COL_CATEGORY).value();
            parentCategoryDict[accNum] = accCategory;
            break;
          }
        }
      }
      // Then check the parent category if it's NMD and if not, add the validator. Otherwise remove it.
      if (parentCategoryDict[parentAccNum] !== nmdCategoryText) {
        sheet.getCell(r, COL_FILTER_CRITERIA).validator(nmdFilterCriteriaValidator);
      } else {
        sheet.getCell(r, COL_FILTER_CRITERIA).validator(null);
      }
    }
  } catch (err) {
    console.error('Unable to find a category for NonMaturity Deposits or Auxiliary Accounts');
  }
};

export const addValidationsToRow = (component: any, row: number) => {
  // For each row, add validations all cells.
  // 1. Category - Needs to be a valid category from => /api/balancesheet/categories
  // 2. Account Name - "The account name already exists."
  // 3. Mnemonic - "The Mnemonic should be unique."
  // 4. Account # - "The Account Number should be unique. "
  // 5. Parent # - "The parent account number is not valid."
  // 6. Display Order - "Two accounts cannot have the same display order."
  // 7. Filter Criteria - Auxiliary accounts cannot have filter criteria, USER_WEIGHT is only available for non-maturity deposits

  const { sheet, total, categoryNamesString, validationMessages, categories } = component;

  addCategoryValidatorToRow(component, row);
  addMnemonicValidatorToRow(sheet, row);
  addAccountNumberValidatorToRow(sheet, row);
  addParentAccountNumberValidatorToRow(sheet, row);
  addDisplayOrderValidatorToRow(sheet, row);
  addFilterCriteriaValidatorToRow(sheet, row, categories);
};
